import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    Autocomplete,
    Typography,
    FormControlLabel,
    Switch,
    Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import pricebookAPI from 'services/pricebook/api';
import { gatherCatalog } from 'services/utils/enums';
import { useAuth } from 'services/auth';

const SearchRules = ({ onSearchResults, onSearchPerformed, setLoading, errorContext }) => {
    const auth = useAuth();
    const [vin, setVin] = useState('');
    const [yearStart, setYearStart] = useState(null);
    const [yearEnd, setYearEnd] = useState(null);
    const [make, setMake] = useState([]);
    const [model, setModel] = useState([]);
    const [vehicleMakes, setVehicleMakes] = useState([]);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [allVehicleModels, setAllVehicleModels] = useState([]);
    const [serviceType, setServiceType] = useState('Keys');
    const [repairType, setRepairType] = useState('');
    const [part, setPart] = useState('');
    const [position, setPosition] = useState('');
    const [catalog, setCatalog] = useState([]);
    const [catalogLoaded, setCatalogLoaded] = useState(false);
    const [useVin, setUseVin] = useState(false);
    const [decodedVehicle, setDecodedVehicle] = useState(null);
    const [estimates, setEstimates] = useState(null);
    const modelFilterTimerRef = useRef(null);

    // Generate year options (current year + 1 down to 1980)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from(
        { length: currentYear - 1979 + 2 },
        (_, i) => currentYear + 1 - i,
    );

    // Fetch vehicle makes and models
    useEffect(() => {
        const fetchVehicleData = async () => {
            try {
                const [makesResponse, modelsResponse] = await Promise.all([
                    pricebookAPI.getVehicleMakes(),
                    pricebookAPI.getVehicleModels(),
                ]);

                if (makesResponse?.data?.payload) {
                    setVehicleMakes(makesResponse.data.payload);
                }

                if (modelsResponse?.data?.payload) {
                    setVehicleModels(modelsResponse.data.payload);
                    setAllVehicleModels(modelsResponse.data.payload);
                }
            } catch (error) {
                console.error('Error fetching vehicle data:', error);
            }
        };
        fetchVehicleData();
    }, []);

    // Debounced model filtering when makes change
    useEffect(() => {
        if (modelFilterTimerRef.current) {
            clearTimeout(modelFilterTimerRef.current);
        }
        modelFilterTimerRef.current = setTimeout(async () => {
            try {
                if (make && Array.isArray(make) && make.length > 0) {
                    const resp = await pricebookAPI.getVehicleModels(make);
                    if (resp?.data?.payload) {
                        const models = resp.data.payload;
                        setVehicleModels(models);
                        // Trim selected models to those available
                        if (Array.isArray(model) && model.length > 0) {
                            const trimmed = model.filter(m => models.includes(m));
                            if (trimmed.length !== model.length) {
                                setModel(trimmed);
                            }
                        }
                    }
                } else {
                    setVehicleModels(allVehicleModels);
                }
            } catch (e) {
                console.error('Error filtering models by make:', e);
            }
        }, 250);
        return () => {
            if (modelFilterTimerRef.current) clearTimeout(modelFilterTimerRef.current);
        };
    }, [make, allVehicleModels, model]);

    // Load catalog when user first opens a dropdown
    const loadCatalogIfNeeded = async () => {
        if (catalogLoaded || !auth.state.loggedIn) return;

        try {
            console.log('Loading catalog...');
            const response = await gatherCatalog({});
            if (response?.data?.payload) {
                setCatalog(response.data.payload);
                console.log('Catalog loaded:', response.data.payload.length, 'items');
                setCatalogLoaded(true);
            }
        } catch (error) {
            console.error('Error loading catalog:', error);
        }
    };

    // Extract unique service types and parts from catalog
    const serviceTypeOptions = useMemo(() => {
        const arr = catalog.map((item) => item.service_type).filter(Boolean);
        const options = arr.filter((v, i, a) => a.indexOf(v) === i).sort();
        console.log('Service type options:', options);
        return options;
    }, [catalog]);

    const repairTypeOptions = useMemo(() => {
        // Filter repair types by selected service type
        const filteredCatalog = serviceType
            ? catalog.filter((item) => item.service_type === serviceType)
            : catalog;

        const arr = filteredCatalog.map((item) => item.repair_type).filter(Boolean);
        const options = arr.filter((v, i, a) => a.indexOf(v) === i).sort();
        console.log('Repair type options for', serviceType, ':', options);
        return options;
    }, [catalog, serviceType]);

    const partOptions = useMemo(() => {
        // Filter parts by selected service type and repair type
        let filteredCatalog = catalog;
        if (serviceType) {
            filteredCatalog = filteredCatalog.filter((item) => item.service_type === serviceType);
        }
        if (repairType) {
            filteredCatalog = filteredCatalog.filter((item) => item.repair_type === repairType);
        }

        const arr = filteredCatalog.map((item) => item.part).filter(Boolean);
        const options = arr.filter((v, i, a) => a.indexOf(v) === i).sort();
        console.log('Part options for', serviceType, repairType, ':', options);
        return options;
    }, [catalog, serviceType, repairType]);

    const positionOptions = useMemo(() => {
        // Filter positions by selected service type, repair type, and part
        let filteredCatalog = catalog;
        if (serviceType) {
            filteredCatalog = filteredCatalog.filter((item) => item.service_type === serviceType);
        }
        if (repairType) {
            filteredCatalog = filteredCatalog.filter((item) => item.repair_type === repairType);
        }
        if (part) {
            filteredCatalog = filteredCatalog.filter((item) => item.part === part);
        }

        const arr = filteredCatalog.map((item) => item.position).filter(Boolean);
        const uniquePositions = arr.filter((v, i, a) => a.indexOf(v) === i).sort();
        console.log('Position options for', serviceType, repairType, part, ':', uniquePositions);
        return uniquePositions;
    }, [catalog, serviceType, repairType, part]);

    // Load initial search results on mount
    useEffect(() => {
        if (auth.state.loggedIn && auth.state.idToken) {
            console.log('Triggering initial search...');
            handleSearch();
        }

    }, [auth.state.loggedIn, auth.state.idToken]);

    // Auto-fetch estimate when VIN is complete (17 characters)
    useEffect(() => {
        if (useVin && vin && vin.length === 17 && auth.state.loggedIn) {
            handleVinEstimate();
        }

    }, [vin, useVin, auth.state.loggedIn]);

    const handleVinEstimate = async () => {
        setLoading(true);
        try {
            /* eslint-disable camelcase */
            const estimateRequest = {
                vehicle: { vin: vin.trim() },
                services: [
                    {
                        service_details: {
                            service_type: serviceType,
                            repair_type: repairType || undefined,
                        },
                    },
                ],
            };
            /* eslint-enable camelcase */

            console.log('Fetching estimate for VIN:', estimateRequest);
            const response = await pricebookAPI.estimate(estimateRequest);
            console.log('Estimate response:', response);

            if (response?.data?.status === 200) {
                const result = response.data.payload;

                // Extract decoded vehicle info
                if (result?.vehicle) {
                    const vehicleInfo = {
                        year: result.vehicle.year,
                        make: result.vehicle.make,
                        model: result.vehicle.model,
                        vin: result.vehicle.vin || vin,
                    };
                    setDecodedVehicle(vehicleInfo);
                    console.log('Decoded vehicle from estimate:', vehicleInfo);
                }

                // Extract part from estimates, but only set position if all estimates have the same position
                let extractedPart = part;
                let extractedPosition = '';
                
                // Collect all estimates to check for multiple positions
                const allEstimates = result?.services?.flatMap(s => s.estimates || []) || [];
                const uniquePositions = [...new Set(allEstimates
                    .map(est => est?.service_details?.position)
                    .filter(Boolean),
                )];
                
                // Extract part from first estimate (part should be consistent across estimates for same key type)
                if (allEstimates[0]?.service_details?.part) {
                    extractedPart = allEstimates[0].service_details.part;
                    setPart(extractedPart);
                }
                
                // Only auto-select position if there's exactly one unique position
                // If there are multiple positions (e.g., US and Asian), leave position empty
                // so the search returns rules for all positions
                if (uniquePositions.length === 1) {
                    extractedPosition = uniquePositions[0];
                    setPosition(extractedPosition);
                    console.log('Single position found, auto-selecting:', extractedPosition);
                } else if (uniquePositions.length > 1) {
                    setPosition('');
                    console.log('Multiple positions found, not auto-selecting:', uniquePositions);
                }

                // Store estimates for display
                setEstimates(result);

                errorContext.DISPATCH({
                    type: 'NO_ERROR',
                    payload: { alert: 'VIN decoded and estimates retrieved successfully' },
                });

                // Auto-trigger search with the populated data
                // Pass extracted values directly to avoid state timing issues
                handleSearch(extractedPart, extractedPosition);
            } else {
                console.log('Estimate failed, response:', response);
                errorContext.DISPATCH({
                    type: 'API_ERROR',
                    payload: { alert: response?.data?.message || 'Failed to get estimate for VIN' },
                });
                setDecodedVehicle(null);
                setEstimates(null);
            }
        } catch (error) {
            console.error('Estimate error:', error);
            errorContext.DISPATCH({
                type: 'API_ERROR',
                payload: { alert: error.message || 'Error getting estimate for VIN' },
            });
            setDecodedVehicle(null);
            setEstimates(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (overridePart, overridePosition) => {
        if (!serviceType) {
            errorContext.DISPATCH({
                type: 'INVALID_INPUT',
                payload: { alert: 'Service Type is required' },
            });
            return;
        }

        // Validate VIN or year/make/model
        if (useVin) {
            if (!vin || vin.trim().length === 0) {
                errorContext.DISPATCH({
                    type: 'INVALID_INPUT',
                    payload: { alert: 'VIN is required when \'Search by VIN\' is selected' },
                });
                return;
            }
        }

        setLoading(true);

        /* eslint-disable camelcase */
        const searchParams = {
            service_type: serviceType,
            include_description: false,
        };

        if (repairType) {
            searchParams.repair_type = repairType;
        }

        if (useVin && vin) {
            // Send VIN for decoding
            searchParams.vin = vin.trim();
        } else {
            // Send year/make/model
            if (yearStart) {
                searchParams.year_start = yearStart;
            }

            if (yearEnd) {
                searchParams.year_end = yearEnd;
            }

            if (make && make.length > 0) {
                searchParams.make = make;
            }

            if (model && model.length > 0) {
                searchParams.model = model;
            }
        }
        /* eslint-enable camelcase */

        // Always send part and position if available (applies to both VIN and manual mode)
        // Use override values if provided (for immediate post-estimate search)
        const partToUse = overridePart !== undefined ? overridePart : part;
        const positionToUse = overridePosition !== undefined ? overridePosition : position;

        if (partToUse) {
            searchParams.part = partToUse;
        }

        if (positionToUse) {
            searchParams.position = positionToUse;
        }

        try {
            console.log('Searching with params:', searchParams);

            // Fetch first page to get total count
            // eslint-disable-next-line camelcase
            const firstPageParams = { ...searchParams, page: 1, page_size: 25 };
            const firstResponse = await pricebookAPI.searchPricebooks(firstPageParams);
            console.log('Search response:', firstResponse);

            if (firstResponse?.data?.status === 200) {
                const payload = firstResponse.data.payload || [];
                const paging = firstResponse.data.paging || {};

                // Extract decoded vehicle info if present in payload
                const decodedVehicleData = payload?.decoded_vehicle;
                if (decodedVehicleData) {
                    setDecodedVehicle(decodedVehicleData);
                    console.log('Decoded vehicle:', decodedVehicleData);
                } else {
                    setDecodedVehicle(null);
                }

                // Extract rules from first page
                let allRules = Array.isArray(payload) ? payload : (payload?.rules || []);

                // If there are more pages, fetch them all
                const total = paging.total_count || paging.total || allRules.length;
                // eslint-disable-next-line camelcase
                const pageSize = paging.page_size || 10;
                const totalPages = Math.ceil(total / pageSize);

                if (totalPages > 1) {
                    console.log(`Fetching ${totalPages - 1} additional pages...`);
                    const additionalPagePromises = [];

                    for (let page = 2; page <= totalPages; page++) {
                        // eslint-disable-next-line camelcase
                        const pageParams = { ...searchParams, page, page_size: pageSize };
                        additionalPagePromises.push(pricebookAPI.searchPricebooks(pageParams));
                    }

                    const additionalResponses = await Promise.all(additionalPagePromises);

                    additionalResponses.forEach(response => {
                        if (response?.data?.status === 200) {
                            const pagePayload = response.data.payload || [];
                            const pageRules = Array.isArray(pagePayload) ? pagePayload : (pagePayload?.rules || []);
                            allRules = [...allRules, ...pageRules];
                        }
                    });
                    
                    console.log(`Fetched total of ${allRules.length} rules across ${totalPages} pages`);
                }

                onSearchResults(allRules);
                if (typeof onSearchPerformed === 'function') {
                    onSearchPerformed(searchParams, allRules);
                }
            } else {
                console.log('Search failed, response:', firstResponse);
                errorContext.DISPATCH({
                    type: 'API_ERROR',
                    payload: { alert: firstResponse?.data?.message || 'Failed to search pricebook' },
                });
                setDecodedVehicle(null);
                const results = [];
                onSearchResults(results);
                if (typeof onSearchPerformed === 'function') {
                    onSearchPerformed(searchParams, results);
                }
            }
        } catch (error) {
            console.error('Search error:', error);
            errorContext.DISPATCH({
                type: 'API_ERROR',
                payload: { alert: error.message || 'Error searching pricebook' },
            });
            setDecodedVehicle(null);
            const results = [];
            onSearchResults(results);
            if (typeof onSearchPerformed === 'function') {
                onSearchPerformed(searchParams, results);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setVin('');
        setYearStart(null);
        setYearEnd(null);
        setMake([]);
        setModel([]);
        setServiceType('Keys');
        setRepairType('');
        setPart('');
        setPosition('');
        setUseVin(false);
        setDecodedVehicle(null);
        setEstimates(null);

        // Trigger search with explicitly cleared params to avoid stale state
        setLoading(true);
        
        /* eslint-disable camelcase */
        const searchParams = {
            service_type: 'Keys', // Default service type
            include_description: false,
        };

        pricebookAPI.searchPricebooks({ ...searchParams, page: 1, page_size: 25 })
            .then(async (firstResponse) => {
                if (firstResponse?.data?.status === 200) {
                    const payload = firstResponse.data.payload || [];
                    const paging = firstResponse.data.paging || {};
                    let allRules = Array.isArray(payload) ? payload : (payload?.rules || []);

                    const total = paging.total_count || paging.total || allRules.length;
                    const pageSize = paging.page_size || 10;
                    const totalPages = Math.ceil(total / pageSize);

                    if (totalPages > 1) {
                        const additionalPagePromises = [];
                        for (let page = 2; page <= totalPages; page++) {
                            additionalPagePromises.push(
                                pricebookAPI.searchPricebooks({ ...searchParams, page, page_size: pageSize }),
                            );
                        }
                        /* eslint-enable camelcase */
                        const additionalResponses = await Promise.all(additionalPagePromises);
                        additionalResponses.forEach(response => {
                            if (response?.data?.status === 200) {
                                const pagePayload = response.data.payload || [];
                                const pageRules = Array.isArray(pagePayload) ? pagePayload : (pagePayload?.rules || []);
                                allRules = [...allRules, ...pageRules];
                            }
                        });
                    }

                    onSearchResults(allRules);
                    if (typeof onSearchPerformed === 'function') {
                        onSearchPerformed(searchParams, allRules);
                    }
                }
            })
            .catch((error) => {
                console.error('Search error after clear:', error);
                onSearchResults([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Card>
            <CardContent>
                <Typography variant='h6' sx={{ mb: 2 }}>
                    Search Pricebook Rules
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={useVin}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        setUseVin(newValue);

                                        // Clear VIN-related fields when toggling off
                                        if (!newValue) {
                                            setVin('');
                                            setDecodedVehicle(null);
                                            setEstimates(null);
                                            setPart('');
                                            setPosition('');
                                        }
                                    }}
                                    color='primary'
                                />
                            }
                            label='Search by VIN'
                        />
                    </Grid>
                    {useVin ? (
                        <>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label='VIN'
                                    value={vin}
                                    onChange={(e) => {
                                        setVin(e.target.value.toUpperCase());
                                        setDecodedVehicle(null); // Clear decoded info when VIN changes
                                        setEstimates(null); // Clear estimates when VIN changes
                                    }}
                                    placeholder='Enter 17-digit VIN'
                                    inputProps={{ maxLength: 17 }}
                                    helperText='Enter the vehicle identification number (VIN)'
                                />
                            </Grid>
                            {decodedVehicle && (
                                <Grid item xs={12}>
                                    <Alert severity='success' sx={{ mt: 1 }}>
                                        <Typography variant='body2' sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Decoded Vehicle:
                                        </Typography>
                                        <Typography variant='body2'>
                                            {decodedVehicle.year}
                                            {' '}
                                            {decodedVehicle.make}
                                            {' '}
                                            {decodedVehicle.model}
                                            {' '}
                                            (VIN: {decodedVehicle.vin})
                                        </Typography>
                                    </Alert>
                                </Grid>
                            )}
                            {estimates?.services && estimates.services.length > 0 && (
                                <Grid item xs={12}>
                                    <Alert severity='info' sx={{ mt: 1 }}>
                                        <Typography variant='body2' sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            Estimates:
                                        </Typography>
                                        <Typography
                                            variant='caption'
                                            sx={{
                                                display: 'block',
                                                mb: 1.5,
                                                fontStyle: 'italic',
                                                color: 'text.secondary',
                                            }}
                                        >
                                            Note: These estimates are from ALL companies, not just your
                                            pricebook. You may not see rules in your pricebook for all
                                            estimates shown.
                                        </Typography>
                                        {estimates.services.map((service, serviceIdx) => (
                                            <Box key={serviceIdx} sx={{ mb: 1 }}>
                                                {service.estimates?.map((est, estIdx) => (
                                                    <Box key={estIdx} sx={{ mb: 1 }}>
                                                        {est.service_details && (
                                                            <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                                                                {est.service_details.service_type}
                                                                {est.service_details.repair_type && ` - ${est.service_details.repair_type}`}
                                                                {est.service_details.part && ` - ${est.service_details.part}`}
                                                                {est.service_details.position && ` (${est.service_details.position})`}
                                                            </Typography>
                                                        )}
                                                        {est.prices && est.prices.length > 0 ? (
                                                            <Box sx={{ ml: 2 }}>
                                                                {est.prices.map((price, priceIdx) => (
                                                                    <Typography key={priceIdx} variant='body2'>
                                                                        • {price.price != null
                                                                            ? `$${price.price.toFixed(2)}`
                                                                            : (
                                                                                <Typography
                                                                                    component='span'
                                                                                    sx={{
                                                                                        fontStyle: 'italic',
                                                                                        color: 'text.secondary',
                                                                                    }}
                                                                                >
                                                                                    Quote Required
                                                                                </Typography>
                                                                            )}
                                                                        {' - '}
                                                                        <strong>{price.name || 'Unknown Company'}</strong>
                                                                        {price.rank != null && ` (Rank: ${price.rank})`}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        ) : (
                                                            <Typography variant='body2' sx={{ ml: 2, fontStyle: 'italic' }}>
                                                                No prices available
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        ))}
                                    </Alert>
                                </Grid>
                            )}
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} md={1.5}>
                                <Autocomplete
                                    value={yearStart}
                                    onChange={(event, newValue) => setYearStart(newValue)}
                                    options={yearOptions}
                                    getOptionLabel={(option) => option.toString()}
                                    renderInput={(params) => (
                                        <TextField {...params} label='Year Start' placeholder='Optional' />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={1.5}>
                                <Autocomplete
                                    value={yearEnd}
                                    onChange={(event, newValue) => setYearEnd(newValue)}
                                    options={yearOptions}
                                    getOptionLabel={(option) => option.toString()}
                                    renderInput={(params) => (
                                        <TextField {...params} label='Year End' placeholder='Optional' />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={vehicleMakes}
                                    value={make}
                                    onChange={(e, newValue) => setMake(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Make'
                                            placeholder='Leave blank for all makes'
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={vehicleModels}
                                    value={model}
                                    onChange={(e, newValue) => setModel(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Model'
                                            placeholder='Leave blank for all models'
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12} md={2}>
                        <Autocomplete
                            value={serviceType}
                            onChange={(event, newValue) => {
                                setServiceType(newValue);
                                // Clear repair type and part when service type changes
                                if (newValue !== serviceType) {
                                    setRepairType('');
                                    setPart('');
                                }
                            }}
                            onOpen={loadCatalogIfNeeded}
                            options={serviceTypeOptions}
                            disabled
                            renderInput={(params) => (
                                <TextField {...params} label='Service Type *' placeholder='Required' />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Autocomplete
                            value={repairType}
                            onChange={(event, newValue) => {
                                setRepairType(newValue);
                                // Clear part when repair type changes
                                if (newValue !== repairType) {
                                    setPart('');
                                }
                            }}
                            onOpen={loadCatalogIfNeeded}
                            options={repairTypeOptions}
                            renderInput={(params) => (
                                <TextField {...params} label='Repair Type' placeholder='Optional' />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Autocomplete
                            value={part}
                            onChange={(event, newValue) => setPart(newValue)}
                            onOpen={loadCatalogIfNeeded}
                            options={partOptions}
                            freeSolo
                            disabled={useVin}
                            renderInput={(params) => (
                                <TextField {...params} label='Part' placeholder='Optional' />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Autocomplete
                            value={position}
                            onChange={(event, newValue) => setPosition(newValue)}
                            onOpen={loadCatalogIfNeeded}
                            options={positionOptions}
                            disabled={useVin}
                            renderInput={(params) => (
                                <TextField {...params} label='Position' placeholder='Optional' />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<SearchIcon />}
                                onClick={() => handleSearch()}
                            >
                                Search
                            </Button>
                            <Button variant='outlined' onClick={handleClear}>
                                Clear
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

SearchRules.propTypes = {
    onSearchResults: PropTypes.func.isRequired,
    onSearchPerformed: PropTypes.func,
    setLoading: PropTypes.func.isRequired,
    errorContext: PropTypes.object.isRequired,
};

export default SearchRules;
